import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Mail, Lock, Eye, EyeOff, Sun, Moon, Store } from 'lucide-react';
import LanguageSwitcher from '../components/common/LanguageSwitcher';

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('admin@sttora.com');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const { login, loading } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const userData = await login(email, password);
      if (userData.role === 'admin') {
        navigate('/admin');
      } else if (userData.role === 'gestor') {
        navigate('/dashboard');
      } else {
        navigate('/store');
      }
    } catch (error) {
      setError(error.message || 'Error al iniciar sesión');
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-black' : 'bg-gradient-to-br from-orange-50 to-orange-100'} flex items-center justify-center p-4 relative`}>
      <div className="absolute top-6 right-6 flex items-center space-x-3">
        <LanguageSwitcher />
        <button
          onClick={toggleTheme}
          className={`p-3 rounded-2xl ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white/80 hover:bg-white'} backdrop-blur-sm shadow-lg transition-all duration-200`}
        >
          {isDarkMode ? <Sun className="w-6 h-6 text-yellow-400" /> : <Moon className="w-6 h-6 text-gray-600" />}
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8"
      >
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-xl p-8`}>
          <div className="text-center">
            {isDarkMode ? (
              <img src="/sttora.png" alt="STTORA" className="h-14 mx-auto mb-4" />
            ) : (
              <div className="inline-block p-2 bg-gray-800 rounded-xl mb-4">
                <img src="/sttora.png" alt="STTORA" className="h-12" />
              </div>
            )}
            <p className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Inicia sesión en tu cuenta</p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 text-red-600 text-sm p-3 rounded-xl">
                {error}
              </div>
            )}

            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`relative block w-full pl-10 pr-3 py-3 border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} ${isDarkMode ? 'text-white' : 'text-gray-900'} ${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded-2xl focus:outline-none focus:ring-orange-500 focus:border-orange-500`}
                  placeholder="Correo electrónico"
                />
              </div>
            </div>

            <div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`relative block w-full pl-10 pr-10 py-3 border ${isDarkMode ? 'border-gray-600' : 'border-gray-300'} ${isDarkMode ? 'text-white' : 'text-gray-900'} ${isDarkMode ? 'bg-gray-700' : 'bg-white'} rounded-2xl focus:outline-none focus:ring-orange-500 focus:border-orange-500`}
                  placeholder="Contraseña"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                </button>
              </div>
            </div>

            <div>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-2xl text-white bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
              >
                {loading ? 'Cargando...' : 'Iniciar Sesión'}
              </motion.button>
            </div>
          </form>

          <div className="mt-6 space-y-3">
            <p className="text-center text-sm text-gray-500">
              ¿No tienes cuenta?{' '}
              <Link to="/register" className="text-orange-600 hover:text-orange-700 font-medium">
                Regístrate gratis
              </Link>
            </p>
            <p className="text-center text-sm text-gray-500">
              ¿Eres Gestor?{' '}
              <Link to="/register/gestor" className="text-orange-600 hover:text-orange-700 font-medium">
                Registro de Gestor
              </Link>
            </p>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center text-xs text-gray-400 space-y-1">
              <p>Demo: admin@sttora.com / Admin123!</p>
              <p>Gestor: gestor1@sttora.com / Demo123!</p>
              <p>Premium: premium1@sttora.com / Demo123!</p>
            </div>
          </div>

          <div className="mt-4 text-center">
            <Link to="/store" className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700">
              <Store className="w-4 h-4" /> Visitar Tienda
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;
